// streaming.ts - Fixed SSE implementation with relative URLs
import type { ApiResult } from '../types/kyb.types';

export interface CompanyInfo {
  company_name: string;
  country: string;
  entity_type?: string;  // "any", "limited_company", "sole_trader", "partnership"
  website?: string;      // Optional website/domain to verify
  industry?: string;
  [key: string]: any;
}

export interface ProgressData {
  status: 'progress' | 'starting' | 'finalizing';
  message?: string;
  progress?: number;
  step?: number;
  total?: number;
  [key: string]: any;
}

export type CompletionResult = ApiResult;

export interface ErrorData {
  error: string;
}

export type ProgressCallback = (data: ProgressData) => void;
export type CompleteCallback = (result: CompletionResult) => void;
export type ErrorCallback = (error: Error) => void;
export type CancelFunction = () => void;

export function streamBusinessVerification(
  companyInfo: CompanyInfo,
  onProgress: ProgressCallback,
  onComplete: CompleteCallback,
  onError: ErrorCallback,
  authToken?: string
): CancelFunction {
  // Use relative URL - nginx proxies to backend
  const url = '/api/verify';
  
  console.log('[SSE] Starting connection to:', url);
  console.log('[SSE] Company info:', companyInfo);
  
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout>;
  
  timeoutId = setTimeout(() => {
    console.error('[SSE] Request timed out after 120 seconds');
    controller.abort();
    onError(new Error('Request timed out after 120 seconds'));
  }, 120000);
  
  // Build headers with optional auth token
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'text/event-stream',
  };
  
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  
  fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ company_info: companyInfo }),
    signal: controller.signal,
  })
  .then(async response => {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    console.log('[SSE] Connection established, status:', response.status);
    
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    
    async function processStream(): Promise<void> {
      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            console.log('[SSE] Stream ended naturally');
            clearTimeout(timeoutId);
            break;
          }
          
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          
          const messages = buffer.split('\n\n');
          buffer = messages.pop() || '';
          
          for (const message of messages) {
            if (!message.trim()) continue;
            
            const lines = message.split('\n');
            
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const jsonStr = line.slice(6).trim();
                
                if (jsonStr === '[DONE]' || jsonStr === '') {
                  continue;
                }
                
                try {
                  const data = JSON.parse(jsonStr);
                  console.log('[SSE] Received data:', {
                    status: data.status,
                    progress: data.progress,
                    hasResult: !!data.result
                  });
                  
                  if (data.status === 'error' || data.error) {
                    console.error('[SSE] Error received:', data.message || data.error);
                    clearTimeout(timeoutId);
                    onError(new Error(data.message || data.error || 'Analysis failed'));
                    reader.cancel();
                    return;
                  }
                  
                  if (data.status === 'completed' && data.result) {
                    console.log('[SSE] Analysis completed successfully');
                    console.log('[SSE] 🚨 RECEIVED RESULT STRUCTURE:');
                    console.log('[SSE]   - companies_house_data:', !!data.result.companies_house_data);
                    if (data.result.companies_house_data) {
                      console.log('[SSE]   - officers array:', data.result.companies_house_data.officers);
                      console.log('[SSE]   - officers count:', data.result.companies_house_data.officers?.length);
                    }
                    console.log('[SSE]   - positive_signals:', data.result.positive_signals?.length);
                    console.log('[SSE]   - negative_signals:', data.result.negative_signals?.length);
                    clearTimeout(timeoutId);
                    onComplete(data.result);
                    reader.cancel();
                    return;
                  }
                  
                  if (data.status === 'stream_end') {
                    console.log('[SSE] Stream end marker received');
                    clearTimeout(timeoutId);
                    reader.cancel();
                    return;
                  }
                  
                  if (data.status && (data.progress !== undefined || data.message)) {
                    onProgress({
                      status: data.status as any,
                      message: data.message || '',
                      progress: data.progress || 0,
                      ...data
                    });
                  }
                  
                } catch (parseError) {
                  console.warn('[SSE] Failed to parse JSON:', jsonStr, parseError);
                }
              }
            }
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('[SSE] Stream aborted');
        } else {
          console.error('[SSE] Stream reading error:', error);
          clearTimeout(timeoutId);
          onError(error as Error);
        }
      } finally {
        reader.releaseLock();
      }
    }
    
    await processStream();
    
  })
  .catch(error => {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      console.log('[SSE] Request aborted');
    } else {
      console.error('[SSE] Connection error:', error);
      onError(error);
    }
  });
  
  return () => {
    console.log('[SSE] Cancellation requested');
    clearTimeout(timeoutId);
    controller.abort();
  };
}
